import { useUISlice } from "@/models/useUISlice";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useAuth } from "@/models/useAuth";
import { useUserSlice } from "@/models/useUserSlice";
import { getPlatform, isNativeApp } from "@/models/platform";
import { PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { gotoPrivacy, gotoTerms } from "@/models/utils";
import { useDismissSheet } from "@/models/useDismissSheet";
import { toast } from "react-hot-toast";
import { logFunnelEvent } from "@/fetchData/datadog";
import { SubscriptionPlan } from "@/models/types";
import {
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  CrownIcon,
} from "@/components/Icons";

const AlertWidgetPaywall = () => {
  const visible = useUISlice((state) => state.paywallVisible);
  const paywallParams: {
    callback?: () => void;
    title?: string;
    subtitle?: string;
    utmSource?: string;
  } = typeof visible === "object" ? visible : {};
  const setVisible = useUISlice((state) => state.setPaywallVisible);
  const { t } = useTranslation();
  const revenueCatPackages = useUISlice((state) => state.revenueCat?.packages);
  const country = useUserSlice((state) => state.country);
  const webSubscriptionPlans = useUserSlice(
    (state) => state.webSubscriptionPlans
  );

  const packages = useMemo(() => {
    if (getPlatform() === "web") return AlertWidgetPaywall.getWebPackages();

    return revenueCatPackages;
  }, [revenueCatPackages, country, webSubscriptionPlans]);

  const yearlyPackage = packages?.find((pkg) =>
    pkg.identifier.includes("annual")
  );
  const weeklyPackage = packages?.find((pkg) =>
    pkg.identifier.includes("weekly")
  );
  const isNative = isNativeApp();

  const [currentPackage, setCurrentPackage] = useState<
    PurchasesPackage | undefined
  >(yearlyPackage);
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);

  useEffect(() => {
    if (!visible) return;

    logFunnelEvent("[10] paywall:viewed", {
      utmSource: paywallParams?.utmSource,
    });

    if (isNative) {
      if (packages && packages.length > 0) {
        setCurrentPackage(yearlyPackage);
      } else if (!packages) {
        useUserSlice
          .getState()
          .subscription()
          .configureRevenueCat({
            onError: () => {
              _onDismiss();
            },
          });
      }
    } else {
      setCurrentPackage(yearlyPackage);
    }
  }, [packages, visible, yearlyPackage, isNative]);

  const _gotoTerms = () => {
    setVisible(undefined);
    gotoTerms();
  };

  const _gotoPrivacy = () => {
    setVisible(undefined);
    gotoPrivacy();
  };

  const _onDismiss = useCallback(() => {
    setVisible(undefined);
    paywallParams?.callback?.();
  }, [setVisible, paywallParams]);

  useDismissSheet(!!visible, _onDismiss);

  const _handlePurchase = () => {
    AlertWidgetPaywall.handlePurchase({
      package: currentPackage,
      utmSource: paywallParams?.utmSource,
      callback: () => {
        _onDismiss();
        toast.success(t("alertWidgetPaywall.subscriptionSuccess"));
      },
    });
  };

  if (!visible) return null;

  if (isNativeApp() && !currentPackage?.product) return <div />;

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col bg-[var(--bg-secondary)]/95 backdrop-blur-lg overflow-hidden">
      <button
        className="absolute z-[100] cursor-pointer w-9 h-9 flex items-center justify-center rounded-full bg-[var(--text-primary)]/10 hover:bg-[var(--text-primary)]/20 active:scale-95 transition-all duration-200 backdrop-blur-md"
        style={{
          top: isNative ? 48 : 24,
          left: 24,
        }}
        onClick={_onDismiss}
      >
        <CloseIcon
          size={22}
          className="text-[var(--text-primary)] opacity-80"
        />
      </button>

      <div className="flex flex-col h-full w-full max-w-lg mx-auto relative">
        {/* Scrollable content */}
        <div className="flex-1 w-full mt-16 overflow-y-auto overflow-x-hidden pb-4 no-scrollbar px-6">
          {/* A. Header */}
          <div className="flex flex-col items-center mb-4">
            <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-tr from-[var(--accent)] to-[var(--accent)]/50 flex items-center justify-center shadow-lg shadow-[var(--accent)]/30">
              <CrownIcon size={32} className="text-white" />
            </div>
            <h2 className="text-3xl font-black text-[var(--text-primary)] text-center tracking-tight leading-tight">
              {paywallParams.title || (
                <>
                  {t("alertWidgetPaywall.title1")}
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--accent)] to-[var(--text-primary)]">
                    {t("alertWidgetPaywall.title2")}
                  </span>{" "}
                  {t("alertWidgetPaywall.title3")}
                </>
              )}
            </h2>
          </div>

          {/* B. Contextual subtitle */}
          <p className="text-[15px] text-[var(--text-secondary)] text-center mb-6">
            {paywallParams.subtitle ||
              t("alertWidgetPaywall.contextSubtitleDefault")}
          </p>

          {/* C. Star rating pill */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--text-primary)]/5 backdrop-blur-sm">
              <span className="text-xs">⭐⭐⭐⭐⭐</span>
              <span className="text-[11px] text-[var(--text-primary)] font-bold tracking-wide uppercase">
                {t("alertWidgetPaywall.rating")}
              </span>
            </div>
          </div>

          {/* D. Comparison table */}
          <div className="mb-8">
            <h3 className="text-base font-bold text-[var(--text-primary)] text-center mb-4">
              {t("landing.comparisonTitle")}
            </h3>
            <div className="rounded-2xl bg-[var(--glass-bg)]/60 backdrop-blur-md overflow-hidden">
              {/* Header row */}
              <div className="grid grid-cols-2 px-4 py-3 border-b border-[var(--text-primary)]/5">
                <span className="text-[11px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">
                  {t("landing.otherApps")}
                </span>
                <span className="text-[11px] font-bold text-[var(--accent)] uppercase tracking-wider text-right">
                  {t("landing.purelyNovels")}
                </span>
              </div>
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`grid grid-cols-2 px-4 py-3 ${i < 4 ? "border-b border-[var(--text-primary)]/5" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <CloseIcon size={14} className="text-red-400 shrink-0" />
                    <span className="text-[13px] text-[var(--text-secondary)]">
                      {t(`landing.comparisonOther${i}`)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-[13px] text-[var(--text-primary)] font-medium text-right">
                      {t(`landing.comparisonOur${i}`)}
                    </span>
                    <CheckIcon size={14} className="text-green-400 shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* E. Features with descriptions */}
          <div className="mb-8 flex flex-col gap-3">
            {[
              { emoji: "📚", num: 1 },
              { emoji: "📱", num: 2 },
              { emoji: "✨", num: 3 },
              { emoji: "🔖", num: 4 },
            ].map(({ emoji, num }) => (
              <div
                key={num}
                className="flex items-start gap-3 p-3 rounded-xl bg-[var(--glass-bg)]/40"
              >
                <span className="text-xl shrink-0 mt-0.5">{emoji}</span>
                <div>
                  <p className="text-[14px] font-bold text-[var(--text-primary)]">
                    {t(`landing.feature${num}Title`)}
                  </p>
                  <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed">
                    {t(`landing.feature${num}Desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* F. Testimonials */}
          <div className="mb-8 flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="p-4 rounded-2xl bg-gradient-to-b from-[var(--glass-bg)] to-[var(--glass-bg)]/50 backdrop-blur-md"
              >
                <p className="text-[14px] italic text-[var(--text-primary)] leading-relaxed">
                  &ldquo;{t(`landing.testimonial${i}Quote`)}&rdquo;
                </p>
                <p className="text-[11px] text-[var(--text-secondary)] font-bold mt-2 uppercase tracking-wider opacity-60">
                  — {t(`landing.testimonial${i}Author`)}
                </p>
              </div>
            ))}
          </div>

          {/* G. FAQ accordion */}
          <div className="mb-4">
            <h3 className="text-base font-bold text-[var(--text-primary)] text-center mb-4">
              {t("landing.faqTitle")}
            </h3>
            <div className="flex flex-col gap-2">
              {[1, 2, 3, 4, 5].map((i) => {
                const isOpen = openFaqIndex === i;
                return (
                  <div
                    key={i}
                    className="rounded-xl bg-[var(--glass-bg)]/40 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : i)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <span className="text-[13px] font-semibold text-[var(--text-primary)] pr-2">
                        {t(`landing.faq${i}Q`)}
                      </span>
                      <ChevronDownIcon
                        size={14}
                        className={`text-[var(--text-secondary)] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <div
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}
                    >
                      <p className="px-4 pb-3 text-[12px] text-[var(--text-secondary)] leading-relaxed">
                        {t(`landing.faq${i}A`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Fixed Bottom Section - Plans & CTA */}
        <div
          className="shrink-0 pt-4 pb-6 px-6 relative z-10"
          style={{
            background:
              "linear-gradient(to top, var(--bg-secondary) 80%, transparent)",
          }}
        >
          {/* Package selector */}
          <div className="flex flex-col gap-3 mb-4">
            {[yearlyPackage, weeklyPackage].map((pkg) => {
              const isAnnual = pkg?.identifier.includes("annual");
              const isSelected = pkg?.identifier === currentPackage?.identifier;

              return (
                <button
                  onClick={() => setCurrentPackage(pkg)}
                  key={pkg?.identifier}
                  className={`group relative flex flex-row items-center justify-between px-5 py-4 rounded-3xl cursor-pointer transition-all duration-300 w-full outline-none border-2
                    ${
                      isSelected
                        ? "border-[var(--accent)] bg-[var(--accent)]/10 shadow-lg shadow-[var(--accent)]/15"
                        : "border-transparent bg-[var(--text-primary)]/5 hover:bg-[var(--text-primary)]/10 active:scale-[0.98]"
                    }`}
                >
                  {isAnnual && (
                    <div className="absolute -top-2.5 right-6 px-2.5 py-0.5 rounded-full z-[1] flex items-center shadow-sm transition-colors duration-300 bg-[var(--accent)]">
                      <span className="text-[9px] font-extrabold uppercase tracking-wider text-[var(--bg-primary)]">
                        {t("alertWidgetPaywall.bestOffer")}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-300
                      ${isSelected ? "border-[var(--accent)] bg-[var(--accent)]" : "border-[var(--text-primary)]/30"}`}
                    >
                      {isSelected && (
                        <CheckIcon
                          size={12}
                          className="text-[var(--bg-primary)]"
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5 items-start">
                      <span className="text-[15px] font-bold text-left transition-colors duration-300 text-[var(--text-primary)]">
                        {isAnnual
                          ? t("alertWidgetPaywall.yearlyAccess")
                          : t("alertWidgetPaywall.weeklyAccess")}
                      </span>
                      {isAnnual && (
                        <span
                          className={`text-[12px] text-left transition-colors duration-300
                          ${isSelected ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`}
                        >
                          {t("alertWidgetPaywall.annualPricing", {
                            price: yearlyPackage?.product.priceString,
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <span className="text-[16px] font-bold transition-colors duration-300 text-[var(--text-primary)]">
                      {pkg?.product.pricePerWeekString}
                    </span>
                    <span
                      className={`text-[11px] transition-colors duration-300
                      ${isSelected ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"}`}
                    >
                      /{t("alertWidgetPaywall.perWeek").toLowerCase()}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* CTA button */}
          <button
            className="w-full h-[60px] rounded-full bg-gradient-to-r from-amber-300 to-amber-400 text-amber-950 font-bold text-[19px] tracking-wide flex items-center justify-center active:scale-[0.98] transition-all shadow-[0_0_15px_rgba(252,211,77,0.5)]"
            onClick={_handlePurchase}
          >
            {t("alertWidgetPaywall.buttonContinue")}
          </button>

          <div className="flex flex-row items-center justify-center gap-1.5 mt-3 opacity-80">
            <div className="p-0.5 rounded-full bg-[var(--success)]/20">
              <CheckIcon size={10} className="text-[var(--success)]" />
            </div>
            <span className="text-[11px] font-medium text-[var(--text-secondary)]">
              {t("alertWidgetPaywall.guaranteeTitle")}
            </span>
          </div>

          <div className="flex flex-row justify-center gap-6 mt-3">
            <button
              onClick={_gotoTerms}
              className="text-[11px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
            >
              {t("alertWidgetPaywall.terms")}
            </button>
            <button
              onClick={_gotoPrivacy}
              className="text-[11px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
            >
              {t("alertWidgetPaywall.privacy")}
            </button>
            {isNative && (
              <button
                onClick={
                  useUserSlice.getState().subscription()
                    .revenueCatRestorePurchase
                }
                className="text-[11px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
              >
                {t("alertWidgetPaywall.restore")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

AlertWidgetPaywall.handlePurchase = async (params: {
  package: PurchasesPackage;
  utmSource?: string;
  callback?: () => void;
}) => {
  if (isNativeApp()) {
    useAuth.signInAsGuest({
      callback: () => {
        useUserSlice.getState().subscription().revenueCatPurchase({
          pkg: params.package,
          utmSource: params.utmSource,
          callback: params.callback,
        });
      },
    });
  } else {
    useAuth.signIn({
      callback: () => {
        useUserSlice
          .getState()
          .subscription()
          .stripeCheckout({
            subscriptionPlan: params.package.identifier as SubscriptionPlan,
          });
      },
    });
  }
};

AlertWidgetPaywall.Footer = () => {
  const { t } = useTranslation();
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const packages = useUISlice((state) => {
    if (getPlatform() === "web") return AlertWidgetPaywall.getWebPackages();

    return state.revenueCat?.packages;
  });
  const yearlyPackage = packages?.find((pkg) =>
    pkg.identifier.includes("annual")
  );
  const weeklyPackage = packages?.find((pkg) =>
    pkg.identifier.includes("weekly")
  );

  const getPerPeriod = (pkg: PurchasesPackage) => {
    let perPeriod = t("alertWidgetPaywall.perWeek");

    if (pkg.identifier.includes("monthly")) {
      perPeriod = t("alertWidgetPaywall.perMonth");
    } else if (pkg.identifier.includes("annual")) {
      perPeriod = t("alertWidgetPaywall.perYear");
    }

    return perPeriod;
  };

  if (isSubscribed || !packages) return null;

  return (
    <div className="p-6 rounded-3xl bg-[var(--bg-secondary)]/80 backdrop-blur-lg shadow-2xl">
      <p className="text-[28px] text-[var(--text-primary)] font-black mb-2 tracking-tight">
        {t("alertWidgetPaywall.footerTitle")}
      </p>
      <p className="text-[15px] leading-6 text-[var(--text-secondary)] font-medium">
        {t("alertWidgetPaywall.footerSubtitle")}
      </p>

      {[yearlyPackage, weeklyPackage].map((pkg, index) => (
        <button
          key={`${pkg.identifier}-${index}`}
          onClick={() => {
            AlertWidgetPaywall.handlePurchase({
              package: pkg,
              utmSource: "paywall_footer",
              callback: () => {},
            });
          }}
          className={`w-full min-h-[56px] py-3 rounded-2xl flex items-center justify-center px-4 transition-all active:scale-[0.98] ${
            pkg?.identifier.includes("weekly")
              ? "mt-3 bg-[var(--text-primary)]/5 hover:bg-[var(--text-primary)]/10 text-[var(--text-secondary)] font-medium"
              : "mt-6 bg-[var(--accent)] text-[var(--bg-primary)] shadow-lg shadow-[var(--accent)]/30 font-bold text-lg"
          }`}
        >
          <span
            className={`${
              pkg?.identifier.includes("weekly")
                ? "text-sm"
                : "text-lg tracking-wide"
            }`}
          >
            {t("alertWidgetPaywall.footerPrice", {
              price: pkg?.product?.priceString,
              period: getPerPeriod(pkg),
            })}
          </span>
        </button>
      ))}

      <div className="flex flex-row justify-center gap-6 mt-4">
        <button
          onClick={gotoTerms}
          className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {t("alertWidgetPaywall.terms")}
        </button>
        <button
          onClick={gotoPrivacy}
          className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {t("alertWidgetPaywall.privacy")}
        </button>

        {isNativeApp() && (
          <button
            onClick={
              useUserSlice.getState().subscription().revenueCatRestorePurchase
            }
            className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          >
            {t("alertWidgetPaywall.restore")}
          </button>
        )}
      </div>
    </div>
  );
};

// Correct calculation for price per week based on annual plan price per year
AlertWidgetPaywall.priceMapper = {
  US: {
    low_tier_weekly: "US$9.99",
    low_tier_annual: "US$99.99",
    mid_tier_weekly: "US$9.99",
    mid_tier_annual: "US$99.99",
    top_tier_weekly: "US$9.99",
    top_tier_annual: "US$99.99",
    yearly_price_per_week: "US$1.92", // 99.99 / 52 ≈ 1.923
  },
  CA: {
    low_tier_weekly: "US$9.99 ≈ CA$13.99",
    low_tier_annual: "US$99.99 ≈ CA$139.99",
    mid_tier_weekly: "US$9.99 ≈ CA$13.99",
    mid_tier_annual: "US$99.99 ≈ CA$139.99",
    top_tier_weekly: "US$9.99 ≈ CA$13.99",
    top_tier_annual: "US$99.99 ≈ CA$139.99",
    yearly_price_per_week: "US$1.92 ≈ CA$2.69", // 139.99 / 52 ≈ 2.692
  },
  UK: {
    low_tier_weekly: "US$9.99 ≈ £7.99",
    low_tier_annual: "US$99.99 ≈ £79.99",
    mid_tier_weekly: "US$9.99 ≈ £7.99",
    mid_tier_annual: "US$99.99 ≈ £79.99",
    top_tier_weekly: "US$9.99 ≈ £7.99",
    top_tier_annual: "US$99.99 ≈ £79.99",
    yearly_price_per_week: "US$1.92 ≈ £1.54", // 79.99 / 52 ≈ 1.538
  },
  FR: {
    low_tier_weekly: "US$9.99 ≈ €9.49",
    low_tier_annual: "US$99.99 ≈ €94.99",
    mid_tier_weekly: "US$9.99 ≈ €9.49",
    mid_tier_annual: "US$99.99 ≈ €94.99",
    top_tier_weekly: "US$9.99 ≈ €9.49",
    top_tier_annual: "US$99.99 ≈ €94.99",
    yearly_price_per_week: "US$1.92 ≈ €1.83", // 94.99 / 52 ≈ 1.826
  },
};

AlertWidgetPaywall.getWebPackages = (): PurchasesPackage[] => {
  const country = useUserSlice.getState().country;
  const { weekly, annual } = useUserSlice.getState().webSubscriptionPlans;

  const weeklyPriceString =
    AlertWidgetPaywall.priceMapper?.[country]?.[weekly] || "US$9.99";
  const annualPriceString =
    AlertWidgetPaywall.priceMapper?.[country]?.[annual] || "US$99.99";
  const yearlyPricePerWeekString =
    (AlertWidgetPaywall.priceMapper?.[country]?.[
      "yearly_price_per_week"
    ] as string) || "US$0.96";

  // Extract numeric price from price string (handles different currencies)
  const extractPrice = (priceString: string): number => {
    const match = priceString.match(/[\d,]+\.?\d*/);
    if (!match) return 0;
    return parseFloat(match[0].replace(/,/g, ""));
  };

  const weeklyPrice = extractPrice(weeklyPriceString);
  const annualPrice = extractPrice(annualPriceString);
  const yearlyPricePerWeek = extractPrice(yearlyPricePerWeekString);

  return [
    {
      identifier: weekly,
      product: {
        price: weeklyPrice,
        priceString: weeklyPriceString,
        pricePerWeekString: weeklyPriceString,
        pricePerWeek: weeklyPrice,
      },
    },
    {
      identifier: annual,
      product: {
        price: annualPrice,
        priceString: annualPriceString,
        pricePerWeekString: yearlyPricePerWeekString,
        pricePerWeek: yearlyPricePerWeek,
      },
    },
  ] as unknown as PurchasesPackage[];
};

export default AlertWidgetPaywall;
